import React from 'react';

import { BreadcrumbsV2 } from '@zola/zola-ui/src/components/BreadcrumbsV2';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import PreauthFooterV3 from '@zola/zola-ui/src/components/PreAuthV3/PreauthFooterV3';
import SEORelatedLinks from '@zola/zola-ui/src/components/SEORelatedLinks';
import ArticleBody from 'components/expert-advice/ArticleBody';
import ContentfulResponsivePicture from 'components/expert-advice/ContentfulResponsivePicture';
import ExpertAdviceFooter from 'components/expert-advice/ExpertAdviceFooter';
import FeaturedArticlesCarousel from 'components/expert-advice/FeaturedArticlesCarousel';
import MarkdownWrapper from 'components/expert-advice/MarkdownWrapper';

import { BreadcrumbV2LinkItemType } from '@zola/zola-ui/src/components/BreadcrumbsV2/BreadcrumbsV2';
import type { WRelatedLinkView } from '@zola/svc-web-api-ts-client';
import { UserContext } from '@zola-helpers/client/dist/es/@types';
import { ExpertAdviceFooterType, PostType } from 'libs/contentful/types';

import H from '@zola/zola-ui/src/typography/Headings';
import P from '@zola/zola-ui/src/typography/Paragraphs';

import { TABLET_MIN } from 'libs/universal/utils/device';

import { PageWrapper } from 'components/LP/v3/PageWrapper.styles';
import { BreadcrumbsContainer } from 'styles/expertAdviceShared.styles';
import styles from './ArticlePage.module.less';

type ArticlePageProps = {
  article: PostType;
  breadcrumbs: BreadcrumbV2LinkItemType[];
  expertAdviceFooter: ExpertAdviceFooterType;
  isPostAuthNav3: boolean;
  relatedLinks: WRelatedLinkView[];
  renderMarketingModuleSection: (i: 1 | 2 | 3 | 4) => JSX.Element;
  userCtx: UserContext | null;
};

const ArticlePage = ({
  article,
  breadcrumbs,
  expertAdviceFooter,
  isPostAuthNav3,
  relatedLinks,
  renderMarketingModuleSection,
  userCtx,
}: ArticlePageProps): JSX.Element => {
  const {
    author,
    body,
    body2,
    body3,
    featured,
    heroCredit,
    heroCreditUrl,
    heroImage,
    excerpt,
    nextUp,
    title,
    tldr,
  } = article;

  return (
    <PageWrapper isPostAuthNav3={isPostAuthNav3}>
      <BreadcrumbsContainer>
        <BreadcrumbsV2 currentSelectionLabel={title} linkItems={breadcrumbs} />
      </BreadcrumbsContainer>
      <div className={styles.articlePageBody}>
        <div className={styles.articlePageBodyInner}>
          <H.TitleAccent1 className={styles.title} presentation="h2">
            {title}
          </H.TitleAccent1>
          <P.BodyLarge className={styles.excerpt}>{excerpt}</P.BodyLarge>
          {author && <P.BodyBase className={styles.author}>By {author}</P.BodyBase>}
          {heroImage?.src && (
            <div className={styles.heroImageContainer}>
              <ContentfulResponsivePicture
                alt={heroImage.altText}
                breakpointWidthMap={{ 1: 380, [TABLET_MIN]: 680 }}
                src={heroImage.src}
                loading="eager"
              />
              {heroCredit && (
                <div className={styles.heroCredit}>
                  Photo by{' '}
                  {heroCreditUrl ? (
                    <LinkV2
                      variant="secondary"
                      href={typeof heroCreditUrl === 'string' ? heroCreditUrl : ''}
                    >
                      {heroCredit}
                    </LinkV2>
                  ) : (
                    <span>{heroCredit}</span>
                  )}
                </div>
              )}
            </div>
          )}
          {tldr && (
            <div className={styles.tldr}>
              <MarkdownWrapper source={tldr} />
            </div>
          )}
          <ArticleBody contentMD={body} {...article} />
          {body2 && <ArticleBody contentMD={body2} />}
          {body3 && <ArticleBody contentMD={body3} />}
        </div>
      </div>
      <div className={styles.featuredArticles}>
        {nextUp && <FeaturedArticlesCarousel articles={nextUp} title="Up next for you" />}
        {featured && <FeaturedArticlesCarousel articles={featured} />}
      </div>
      {relatedLinks.length > 0 && (
        <div className={styles.seoRelatedLinks}>
          <SEORelatedLinks title="Even more answers" relatedLinks={relatedLinks} />
        </div>
      )}
      {renderMarketingModuleSection(1)}
      <BreadcrumbsContainer borderBottom onlyMobile>
        <BreadcrumbsV2 currentSelectionLabel={title} disableBackLink linkItems={breadcrumbs} />
      </BreadcrumbsContainer>
      <ExpertAdviceFooter expertAdviceFooter={expertAdviceFooter} noBorder />
      <PreauthFooterV3 isGuest={userCtx ? Boolean(userCtx.is_guest) : true} />
    </PageWrapper>
  );
};

export default ArticlePage;
